import { CheckboxGroupType, DateRangeType, DateTimePickerValueType, FieldOption, NumberRangeType, RangePicker } from '@nagel-types';
import { capitalizeFirstLetter, convertDateTimePickerValueToDate, formatToLocalDate, getEndOfDay, getFormattedTimezoneAdjustedDate, getStartOfDay } from '@nagel-utils';
import {
    CHECKBOX_GROUP_KEY_VALUES_MAP,
    CHECKBOX_GROUP_RANGE_KEY_VALUES_MAP,
    FILTER_OPERATORS,
    FILTER_STRATEGY_TYPES,
    FilterOperatorType
} from '../configuration/consts/filterStrategiesConsts';
import {
    FILTER_END_DATE_RANGE_KEY,
    FILTER_START_DATE_RANGE_KEY,
    FILTERS_CALC_FIELDS_KEYS,
    NUMBER_RANGE_END_KEY,
    NUMBER_RANGE_START_KEY
} from '@nagel-consts';
import { CheckboxGroupRangeType, FilterCriteria, FilterLabelsType } from '../../models/filterServiceTypes';

const {
    INPUT_STRATEGY,
    NUMBER_INPUT_STRATEGY,
    DATE_PICKER_STRATEGY,
    TODAY_STRATEGY,
    DATE_RANGE_STRATEGY,
    DATE_TIME_RANGE_STRATEGY,
    AUTOCOMPLETE_STRATEGY,
    CHECKBOX_GROUP_STRATEGY,
    CHECKBOX_GROUP_RANGE_STRATEGY,
    INPUT_NUMBER_RANGE_STRATEGY,
    EXTERNAL_FILTER_STRATEGY,
    MULTISELECT_AUTOCOMPLETE_STRATEGY
} = FILTER_STRATEGY_TYPES;

export const DATE_FILTER_FIELD_NAME = 'PlannedDepartureTime';

interface FilterStrategy<T> {
    getValuesHelperText?(value: unknown, labelsMap?: FilterLabelsType): string;
    applyStrategy(key: string, value: unknown, operator?: FilterOperatorType | null): void;
    isApplicable(type: string, key: string): boolean;
    shouldAddFilter(value: unknown): boolean;
}

export abstract class BaseFilterStategy<T> implements FilterStrategy<T> {
    constructor() {
        if (this.constructor === BaseFilterStategy) {
            throw new Error('Abstract classes can\'t be instantiated.');
        }
    }

    getIsCalc = (key: string): boolean => FILTERS_CALC_FIELDS_KEYS.includes(key);

    shouldAddFilter(value: unknown): boolean {
        return !!value;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getValuesHelperText(_value: unknown, _labelsMap?: FilterLabelsType): string {
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    applyStrategy(_key: string, _value: unknown, _operator?: FilterOperatorType | null): T {
        throw new Error('Method not implemented.');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isApplicable(_type: string, _key = ''): boolean {
        return false;
    }
}

export class ExternalFilterStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        EXTERNAL_FILTER_STRATEGY
    ];

    override shouldAddFilter(value: unknown): boolean {
        return !!(value && (Array.isArray(value) ? value.length : value));
    }

    override applyStrategy(key: string, value: string[], operator?: FilterOperatorType | null): FilterCriteria {
        return {
            fieldName: capitalizeFirstLetter(key),
            filterOperator: operator || FILTER_OPERATORS.ONE_OF,
            value,
            ...(this.getIsCalc(key) && { isCalc: true })
        };
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class StringInputStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        INPUT_STRATEGY
    ];

    override applyStrategy(key: string, value: string, operator?: FilterOperatorType | null): FilterCriteria {
        return {
            fieldName: capitalizeFirstLetter(key),
            filterOperator: operator || FILTER_OPERATORS.LIKE,
            value,
            ...(this.getIsCalc(key) && { isCalc: true })
        };
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class NumberInputStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        NUMBER_INPUT_STRATEGY
    ];

    override applyStrategy(key: string, value: string, operator?: FilterOperatorType | null): FilterCriteria {
        const parsedValue = Number.isNaN(parseFloat(value))
            ? value
            : parseFloat(value);

        return {
            fieldName: capitalizeFirstLetter(key),
            filterOperator: operator || FILTER_OPERATORS.EQUAL,
            value: parsedValue
        };
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class DateFieldStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        DATE_PICKER_STRATEGY
    ];

    dateOperators: { [key: string]: string } = {
        startDate: FILTER_OPERATORS.GREATER_THAN_OR_EQUAL,
        endDate: FILTER_OPERATORS.LESS_THAN_OR_EQUAL
    };

    override getValuesHelperText(_value: string | Date): string {
        const parsedDate = _value ? formatToLocalDate(new Date(_value)) : null;

        return parsedDate ? parsedDate : '';
    }

    override applyStrategy(key: string, value: string): FilterCriteria {
        return {
            fieldName: DATE_FILTER_FIELD_NAME,
            filterOperator: this.dateOperators[key],
            value
        };
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class TodayCheckBoxStrategy extends BaseFilterStategy<FilterCriteria[]> {
    supportedFieldKey = TODAY_STRATEGY;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    override applyStrategy(_key: string, _value: string): FilterCriteria[] {
        const startOfDay = getStartOfDay(new Date());
        const endOfDay = getEndOfDay(new Date());

        return [
            {
                fieldName: DATE_FILTER_FIELD_NAME,
                filterOperator: FILTER_OPERATORS.GREATER_THAN_OR_EQUAL,
                value: getFormattedTimezoneAdjustedDate(startOfDay)
            },
            {
                fieldName: DATE_FILTER_FIELD_NAME,
                filterOperator: FILTER_OPERATORS.LESS_THAN_OR_EQUAL,
                value: getFormattedTimezoneAdjustedDate(endOfDay)
            }
        ];
    }

    override isApplicable(_: string, key: string): boolean {
        return key === this.supportedFieldKey;
    }
}

export class DateRangeFieldStrategy extends BaseFilterStategy<FilterCriteria[]> {
    supportedTypes = [
        DATE_RANGE_STRATEGY
    ];

    override shouldAddFilter(value: RangePicker): boolean {
        return !!(value && (value[FILTER_START_DATE_RANGE_KEY] || value[FILTER_END_DATE_RANGE_KEY]));
    }

    override getValuesHelperText(value: RangePicker): string {
        const { startDate, endDate } = value || {};
        const parsedStartDate = startDate ? formatToLocalDate(new Date(startDate)) : null;
        const parsedEndDate = endDate ? formatToLocalDate(new Date(endDate)) : null;

        let message = $localize`${parsedStartDate} to ${parsedEndDate}`;

        if (!parsedStartDate) {
            message = $localize`Up to ${parsedEndDate}`;
        }

        if (!parsedEndDate) {
            message = $localize`From ${parsedStartDate}`;
        }

        if (!parsedStartDate && !parsedEndDate) {
            message = '';
        }

        return message;
    }

    override applyStrategy(key: string, value: RangePicker): FilterCriteria[] {
        return [
            ...(
                value[FILTER_START_DATE_RANGE_KEY]
                    ? [{
                        fieldName: key,
                        filterOperator: FILTER_OPERATORS.GREATER_THAN_OR_EQUAL,
                        value: getFormattedTimezoneAdjustedDate(getStartOfDay(new Date(value[FILTER_START_DATE_RANGE_KEY])))
                    }]
                    : []
            ),
            ...(
                value[FILTER_END_DATE_RANGE_KEY]
                    ? [
                        {
                            fieldName: key,
                            filterOperator: FILTER_OPERATORS.LESS_THAN_OR_EQUAL,
                            value: getFormattedTimezoneAdjustedDate(getEndOfDay(new Date(value[FILTER_END_DATE_RANGE_KEY])))
                        }
                    ]
                    : []
            )
        ];
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class DateTimeRangeFieldStrategy extends BaseFilterStategy<FilterCriteria[]> {
    supportedTypes = [
        DATE_TIME_RANGE_STRATEGY
    ];

    override shouldAddFilter(value: DateTimePickerValueType | null): boolean {
        return !!(value && (value.date?.startDate || value.date?.endDate));
    }

    private getStartInputValue = (value: DateTimePickerValueType): string => {
        const startDate = value.date?.startDate;
        const startTime = value.time?.startTime;
        const parsedStartDate = formatToLocalDate(new Date(startDate as Date | string));

        return startDate
            ?  `${parsedStartDate} ${startTime.hours}:${startTime.minutes}`
            : '';
    };

    private getEndInputValue = (value: DateTimePickerValueType) => {
        const endDate = value.date?.endDate;
        const endTime = value.time?.endTime;
        const parsedEndDate = formatToLocalDate(new Date(endDate as Date | string));

        return endDate
            ?  `${parsedEndDate} ${endTime.hours}:${endTime.minutes}`
            : '';
    };

    override getValuesHelperText(value: DateTimePickerValueType | null): string {
        return value && this.shouldAddFilter(value)
            ? this.getStartInputValue(value) + ' to ' + this.getEndInputValue(value)
            : '';
    }

    override applyStrategy(key: string, value: DateTimePickerValueType): FilterCriteria[] {
        return [
            ...(
                value.date?.startDate
                    ? [{
                        fieldName: key,
                        filterOperator: FILTER_OPERATORS.GREATER_THAN_OR_EQUAL,
                        value: convertDateTimePickerValueToDate(value.date?.startDate.toString(), value.time.startTime).toISOString()
                    }]
                    : []
            ),
            ...(
                value.date?.endDate
                    ? [
                        {
                            fieldName: key,
                            filterOperator: FILTER_OPERATORS.LESS_THAN_OR_EQUAL,
                            value: convertDateTimePickerValueToDate(value.date?.endDate.toString(), value.time.endTime).toISOString()
                        }
                    ]
                    : []
            )
        ];
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class AutocompleteStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        AUTOCOMPLETE_STRATEGY
    ];

    override getValuesHelperText(value: unknown = ''): string {
        return `${value}`;
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class MultiselectAutocompleteStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        MULTISELECT_AUTOCOMPLETE_STRATEGY
    ];

    override shouldAddFilter(filterValue: FieldOption[]): boolean {
        return filterValue.length > 0;
    }

    override getValuesHelperText(value: FieldOption[] = []): string {
        return value.length
            ? value.map((fieldOption) => fieldOption.viewValue).join(', ')
            : '';
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }

    override applyStrategy(key: string, value: FieldOption[], operator?: FilterOperatorType | null): FilterCriteria {
        return {
            fieldName: key,
            filterOperator: operator || FILTER_OPERATORS.ONE_OF,
            value: value.map((option) => option.value)
        };
    }
}
export class CheckboxGroupStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        CHECKBOX_GROUP_STRATEGY
    ];

    override shouldAddFilter(filterValue: CheckboxGroupType): boolean {
        return Object.values(filterValue).some((value) => value);
    }

    override getValuesHelperText(filterValue: string | DateRangeType | NumberRangeType | CheckboxGroupType, labelsMap?: FilterLabelsType): string {
        const valuesToShow = Object.entries(filterValue).reduce<string[]>((acc, [key, value]) => {
            if (value) {
                acc.push(labelsMap ? labelsMap[key] : value);
            }

            return acc;
        }, []);

        return valuesToShow.join(', ');
    }

    override applyStrategy(key: string, checkboxesValue: CheckboxGroupType): FilterCriteria {
        const values = Object.entries(checkboxesValue).reduce<(number | string)[]>((acc, [valueKey, value]) => {
            const filterValue = parseInt(valueKey)
                ? parseInt(valueKey)
                : CHECKBOX_GROUP_KEY_VALUES_MAP[valueKey];

            if (filterValue && value) {
                acc.push(filterValue);
            }

            return acc;
        }, []);

        return {
            fieldName: capitalizeFirstLetter(key),
            filterOperator: FILTER_OPERATORS.ONE_OF,
            value: values,
            ...(this.getIsCalc(key) && { isCalc: true })
        };

    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class CheckboxGroupRangeStrategy extends BaseFilterStategy<FilterCriteria> {
    supportedTypes = [
        CHECKBOX_GROUP_RANGE_STRATEGY
    ];

    override shouldAddFilter(filterValue: CheckboxGroupType): boolean {
        return Object.values(filterValue).some((value) => value);
    }

    override getValuesHelperText(filterValue: string | DateRangeType | NumberRangeType | CheckboxGroupType, labelsMap?: FilterLabelsType): string {
        const valuesToShow = Object.entries(filterValue).reduce<string[]>((acc, [key, value]) => {
            if (value) {
                acc.push(labelsMap ? labelsMap[key] : value);
            }

            return acc;
        }, []);

        return valuesToShow.join(', ');
    }

    override applyStrategy(key: string, filterValue: CheckboxGroupType): FilterCriteria {
        const values = Object.entries(filterValue).reduce<CheckboxGroupRangeType[]>((acc, [valueKey, value]) => {
            const mappedValue = CHECKBOX_GROUP_RANGE_KEY_VALUES_MAP[valueKey];
            if (value) {
                acc.push(mappedValue);
            }

            return acc;
        }, []);

        return {
            fieldName: capitalizeFirstLetter(key),
            filterOperator: FILTER_OPERATORS.ONE_OF_BETWEEN,
            value: values, //Check why was set to be values at first point
            ...(this.getIsCalc(key) && { isCalc: true })

        };

    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}

export class InputNumberRangeStrategy extends BaseFilterStategy<FilterCriteria[]> {
    supportedTypes = [
        INPUT_NUMBER_RANGE_STRATEGY
    ];

    override shouldAddFilter(filterValue: CheckboxGroupType): boolean {
        return Object.values(filterValue).some((value) => value);
    }

    override getValuesHelperText(value: unknown = ''): string {
        const { from, to } = value as {[key:string]: string};

        let message = $localize`${from} to ${to}`;

        if (!from) {
            message = $localize`Up to ${to}`;
        }

        if (!to) {
            message = $localize`From ${from}`;
        }

        if (!from && !to) {
            message = '';
        }

        return message;
    }

    override applyStrategy(key: string, value: { from: string, to: string }): FilterCriteria[] {
        return [
            ...(
                value[NUMBER_RANGE_START_KEY]
                    ? [{
                        fieldName: key,
                        filterOperator: FILTER_OPERATORS.GREATER_THAN_OR_EQUAL,
                        value: parseInt(value[NUMBER_RANGE_START_KEY])
                    }]
                    : []
            ),
            ...(
                value[NUMBER_RANGE_END_KEY]
                    ? [
                        {
                            fieldName: key,
                            filterOperator: FILTER_OPERATORS.LESS_THAN_OR_EQUAL,
                            value: parseInt(value[NUMBER_RANGE_END_KEY])
                        }
                    ]
                    : []
            )
        ];
    }

    override isApplicable(type: string): boolean {
        return this.supportedTypes.includes(type);
    }
}